import React, { memo, useCallback, useRef, useState } from 'react';
import toTitleCase from '@hooks/toTitleCase';
import apis from '@api/api';
import { HANDLE_FAVOURITE } from '@reduxLocal/actions/types';
import { DefaultRootState } from '@reduxLocal/persistState';
import {FlatList, Pressable, TextInput, View, Text, Platform, useWindowDimensions } from 'react-native';
import { FontAwesome, MaterialIcons } from '@expo/vector-icons';
import { Progress, XStack, YStack, Card, H4, SizableText, Popover } from 'tamagui';
import moment from 'moment';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import styles from '@stylesheet';
import { useDispatch, useSelector } from 'react-redux';
import { selectUserId, selectFavouriteProjects } from '@reduxLocal/selectors/userSelectors';
import { useNavigation } from '@react-navigation/native';
import Button from '@components/UI/Button/Button';
import AvatarHandler from '@components/Global/AvatarHandler/AvatarHandler';
import { useDeviceType } from '@hooks/useDeviceType';
import { t } from 'i18next';
import getDaysAgo from '@utils/getDaysAgo';

export interface RenderProjectProps {
  item: any;
  index: number;
  horizontal: boolean;
  _handleSelectUser: (userId: any) => void;
  _handleShareUser: (project: any) => void;
  _handleModalState: (item: any) => void;
  navigation: any;
  qrCodeCallback: (item: any) => void;
  complete?: boolean;
  isTesting?: boolean;
  /**
   * @description Maximum width for the project item, used for horizontal scrolling
   * @default undefined
   * @type {number}
   * */
  maxWidth?: number;
}

const RenderProject = ({ isTesting, maxWidth, qrCodeCallback, complete, navigation, item, index, horizontal, _handleSelectUser, _handleShareUser, _handleModalState }: RenderProjectProps) => {
  const dispatch = useDispatch();
  const ProjectItem = ({ item }) => {
    const project = item;
    const [projectTitle, setProjectTitle] = useState(toTitleCase(item.title));
    const [descriptionEditable, setDescriptionEditable] = useState(false);
    const [context, setContext] = useState(false);
    const [projectDescription, setProjectDescription] = useState(item.description);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const owner = item.createdBy._id;
    const userId = useSelector(selectUserId);
    const favouriteProjects = useSelector(selectFavouriteProjects);
    const theme = themeSelector();
    const { isTablet } = useDeviceType();
    const { width } = useWindowDimensions();
    const globalDate = useSelector((state: DefaultRootState) => state.UI.dateFormat);

    // Responsive variables
    const isSmallScreen = width < 600;
    const isMediumScreen = width >= 600 && width < 1024;
    const showCompactMode = isSmallScreen || (maxWidth && maxWidth < 350);

    // Utility functions
    const isOwner = useCallback(() => {
      return owner === userId;
    }, [owner, userId]);

    const _handleChangeProjectTitle = async () => {
      if (projectTitle === item.title) return setProjectTitle(item.title);
      await apis.changeProjectTitle(item._id, projectTitle).then((res) => {
        if (res.status === 203) {
          setProjectTitle(projectTitle)
        } else {
          setProjectTitle(item.title);
        }
      })
    }

    const _handleDescriptionChange = async () => {
      await apis.changeProjectDescription(item._id, projectDescription).then((res) => {
        if (res.status === 203) {
          setProjectDescription(projectDescription);
        }
      })
    }

    const _handleFavourite = useCallback(async () => {
      dispatch({ type: HANDLE_FAVOURITE, payload: { id: item._id, type: 'projects' } })
      await apis.addFavourite(item._id, 'projects', {});
    }, [dispatch, item._id])

    const handleNavigateToProject = useCallback(() => {
      Platform.OS === 'web'
        ? navigation.replace('IndividualProjectPage', { projectId: item._id, title: item.title })
        : navigation.navigate('IndividualProjectPage', { project: { title: item.title, components: item, _id: item._id } });
    }, [navigation, item._id, item.title, item]);

    return (
      <View
        style={[
          styles.modernCard,
          {
            backgroundColor: theme ? 'white' : 'rgb(51,51,51)',
            margin: showCompactMode ? 8 : 12,
            minHeight: showCompactMode ? 320 : 380,
            // maxWidth: horizontal ? (maxWidth || 600) : undefined,
            // minWidth: horizontal ? '100%' : undefined,
            maxWidth:"99%"
          }
        ]}
      >
        {/* Header Section */}
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          padding: showCompactMode ? 12 : 16,
          borderBottomWidth: 1,
          borderBottomColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
        }}>
          {/* Avatar */}
          <AvatarHandler
            avatar={item.createdBy.profile_information?.avatar?.img}
            avatarImageStyle={{
              width: showCompactMode ? 36 : 44,
              height: showCompactMode ? 36 : 44,
              borderRadius: showCompactMode ? 18 : 22,
              borderWidth: 2,
              borderColor: theme ? '#e2e8f0' : '#334155',
            }}
            replacementImageStyle={{
              width: showCompactMode ? 36 : 44,
              height: showCompactMode ? 36 : 44,
              borderRadius: showCompactMode ? 18 : 22,
            }}
          />

          {/* Title and Metadata */}
          <View style={{ flex: 1, marginLeft: 12 }}>
            <TextInput
              style={{
                fontSize: showCompactMode ? 16 : 18,
                fontWeight: '600',
                color: theme ? "#1e293b" : "#f1f5f9",
                padding: 0,
                margin: 0,
              }}
              value={projectTitle}
              onChangeText={setProjectTitle}
              onBlur={_handleChangeProjectTitle}
            />

            <XStack alignItems="center" gap={8} marginTop={4}>
              <SizableText
                fontSize={12}
                color={theme ? "#64748b" : "#94a3b8"}
              >
                Created {getDaysAgo(item.createdAt)}
              </SizableText>
              <View style={{
                width: 4,
                height: 4,
                borderRadius: 2,
                backgroundColor: theme ? "#64748b" : "#94a3b8",
              }} />
              <SizableText
                fontSize={12}
                color={theme ? "#64748b" : "#94a3b8"}
              >
                {item.team?.length || 0} members
              </SizableText>
            </XStack>

            {complete && (
              <SizableText
                fontSize={12}
                color={theme ? "#dc2626" : "#f87171"}
                marginTop={2}
              >
                Ended {moment(item.end_date).format(globalDate)}
              </SizableText>
            )}
          </View>

          {/* Actions Menu */}
          <XStack alignItems="center" position="absolute" top={-17} right={-10} gap={8}>
            {/* QR Code Button */}
            <Pressable
              onPress={() => qrCodeCallback(item)}
              style={{
                padding: 8,
                borderRadius: 20,
                backgroundColor: theme ? '#f8fafc' : '#1e293b',
              }}
            >
              <MaterialIcons
                name="qr-code"
                size={16}
                color={theme ? "#64748b" : "#94a3b8"}
              />
            </Pressable>

            {/* Favorite Button */}
            <Pressable
              onPress={_handleFavourite}
              style={{
                padding: 8,
                borderRadius: 20,
                backgroundColor: theme ? '#f8fafc' : '#1e293b',
              }}
            >
              <FontAwesome
                name={favouriteProjects?.includes?.(item._id) ? "heart" : "heart-o"}
                size={16}
                color={favouriteProjects?.includes?.(item._id) ? "#ef4444" : (theme ? "#64748b" : "#94a3b8")}
              />
            </Pressable>

            {/* Ellipsis Menu */}
            <Popover
              open={isMenuOpen}
              onOpenChange={setIsMenuOpen}
              placement="bottom-end"
            >
              <Popover.Trigger asChild>
                <Pressable
                  style={{
                    padding: 8,
                    borderRadius: 20,
                    backgroundColor: theme ? '#f8fafc' : '#1e293b',
                  }}
                >
                  <MaterialIcons
                    name="more-vert"
                    size={16}
                    color={theme ? "#64748b" : "#94a3b8"}
                  />
                </Pressable>
              </Popover.Trigger>

              <Popover.Content
                borderWidth={1}
                borderColor={theme ? "$borderColor" : "$borderColorDark"}
                backgroundColor={theme ? "$background" : "$backgroundDark"}
                padding={0}
                minWidth={180}
                borderRadius={12}
                elevation={5}
              >
                <YStack>
                  {/* Team Count */}
                  <View style={{
                    padding: 12,
                    alignItems: 'center',
                    borderBottomWidth: 1,
                    borderBottomColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                  }}>
                    <SizableText fontSize={18} fontWeight="600" color={theme ? "#1e293b" : "#f1f5f9"}>
                      {item.team?.length || 0}
                    </SizableText>
                    <SizableText fontSize={12} color={theme ? "#64748b" : "#94a3b8"}>
                      Team Members
                    </SizableText>
                  </View>

                  {/* Share userId */}
                  <Pressable
                    onPress={() => {
                      setIsMenuOpen(false);
                      _handleShareUser(project);
                    }}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      padding: 12,
                      gap: 12,
                    }}
                  >
                    <MaterialIcons
                      name="person-add"
                      size={18}
                      color={theme ? "#0ea5e9" : "#38bdf8"}
                    />
                    <SizableText fontSize={14} color={theme ? "#1e293b" : "#f1f5f9"}>
                      Add Member
                    </SizableText>
                  </Pressable>

                  <View style={{
                    height: 1,
                    backgroundColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                    marginHorizontal: 12,
                  }} />

                  {/* Edit Project */}
                  <Pressable
                    onPress={() => {
                      setIsMenuOpen(false);
                      setDescriptionEditable(!descriptionEditable);
                    }}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      padding: 12,
                      gap: 12,
                    }}
                  >
                    <MaterialIcons
                      name="edit"
                      size={18}
                      color={theme ? "#059669" : "#10b981"}
                    />
                    <SizableText fontSize={14} color={theme ? "#1e293b" : "#f1f5f9"}>
                      Edit Description
                    </SizableText>
                  </Pressable>

                  {/* Delete */}
                  {isOwner() && (
                    <>
                      <View style={{
                        height: 1,
                        backgroundColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                        marginHorizontal: 12,
                      }} />

                      <Pressable
                        onPress={() => {
                          setIsMenuOpen(false);
                          _handleModalState(item);
                        }}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          padding: 12,
                          gap: 12,
                        }}
                      >
                        <MaterialIcons
                          name="delete"
                          size={18}
                          color="#ef4444"
                        />
                        <SizableText fontSize={14} color="#ef4444">
                          Delete Project
                        </SizableText>
                      </Pressable>
                    </>
                  )}
                </YStack>
              </Popover.Content>
            </Popover>
          </XStack>
        </View>

        {/* Content Section */}
        <View style={{ flex: 1, padding: showCompactMode ? 12 : 5 }}>
          {/* Progress Bar */}
          <View style={{ marginBottom: 16 }}>
            <XStack justifyContent="space-between" alignItems="center" marginBottom={8}>
              <SizableText fontSize={12} color={theme ? "#64748b" : "#94a3b8"}>
                Progress
              </SizableText>
              <SizableText fontSize={12} color={theme ? "#64748b" : "#94a3b8"}>
                50%
              </SizableText>
            </XStack>
            <View style={{
              height: 8,
              backgroundColor: theme ? '#f1f5f9' : '#334155',
              borderRadius: 4,
              overflow: 'hidden',
            }}>
              <View style={{
                width: '50%',
                height: '100%',
                backgroundColor: theme ? '#3b82f6' : '#60a5fa',
                borderRadius: 4,
              }} />
            </View>
          </View>

          {/* Project Dates */}
          <View style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            padding: 12,
            backgroundColor: theme ? '#f8fafc' : '#1e293b',
            borderRadius: 8,
            marginBottom: 16,
          }}>
            <View>
              <SizableText fontSize={10} color={theme ? "#64748b" : "#94a3b8"}>
                Start Date
              </SizableText>
              <SizableText fontSize={12} fontWeight="600" color={theme ? "#1e293b" : "#f1f5f9"}>
                {moment(item?.start_date).format(globalDate)}
              </SizableText>
            </View>
            <View style={{ alignItems: 'flex-end' }}>
              <SizableText fontSize={10} color={theme ? "#64748b" : "#94a3b8"}>
                End Date
              </SizableText>
              <SizableText fontSize={12} fontWeight="600" color={theme ? "#1e293b" : "#f1f5f9"}>
                {moment(item?.end_date).format(globalDate)}
              </SizableText>
            </View>
          </View>

          {/* Description */}
          {item.description && (
            <View style={{
              backgroundColor: theme ? '#f8fafc' : '#1e293b',
              borderRadius: 8,
              padding: 12,
              marginBottom: 16,
            }}>
              <XStack alignItems="center" justifyContent="space-between" marginBottom={8}>
                <SizableText fontSize={12} fontWeight="600" color={theme ? "#1e293b" : "#f1f5f9"}>
                  Description
                </SizableText>
                <Pressable onPress={() => setDescriptionEditable(!descriptionEditable)}>
                  <MaterialIcons
                    name="edit"
                    size={16}
                    color={descriptionEditable ? (theme ? "#3b82f6" : "#60a5fa") : (theme ? "#64748b" : "#94a3b8")}
                  />
                </Pressable>
              </XStack>
              <TextInput
                multiline
                editable={descriptionEditable}
                value={projectDescription}
                onChangeText={setProjectDescription}
                onBlur={_handleDescriptionChange}
                style={{
                  color: theme ? "#475569" : "#cbd5e1",
                  fontSize: 12,
                  fontStyle: 'italic',
                  minHeight: 40,
                  textAlignVertical: 'top',
                }}
                placeholder="Add project description..."
                placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
              />
            </View>
          )}

          {/* Work Packages (Horizontal only) */}
          {horizontal && item.work_packages && item.work_packages.length > 0 && (
            <View style={{ marginBottom: 16 }}>
              <SizableText fontSize={12} fontWeight="600" color={theme ? "#1e293b" : "#f1f5f9"} marginBottom={8}>
                Work Packages
              </SizableText>
              <FlatList
                data={item.work_packages}
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={{ gap: 8 }}
                renderItem={({ item: workPackage }) => (
                  <View style={{
                    backgroundColor: theme ? '#f1f5f9' : '#334155',
                    padding: 8,
                    borderRadius: 6,
                    minWidth: 120,
                  }}>
                    <SizableText fontSize={11} numberOfLines={2} color={theme ? "#475569" : "#cbd5e1"}>
                      {workPackage.work_package_title}
                    </SizableText>
                  </View>
                )}
                keyExtractor={(workPackage) => workPackage._id}
              />
            </View>
          )}

          {/* Team Members */}
          {item.team && item.team.length > 0 && (
            <View>
              <SizableText fontSize={12} fontWeight="600" color={theme ? "#1e293b" : "#f1f5f9"} marginBottom={8}>
                Team Members
              </SizableText>
              <XStack gap={8} flexWrap="wrap">
                {item.team.slice(0, 5).map((member, idx) => (
                  <View
                    key={member._id}
                    animation="fadeIn"
                    delay={idx * 100}
                    style={{ alignItems: 'center' }}
                  >
                    <AvatarHandler
                      avatar={member.profile_information?.avatar?.img}
                      avatarImageStyle={{
                        width: 32,
                        height: 32,
                        borderRadius: 16,
                        borderWidth: 2,
                        borderColor: theme ? '#e2e8f0' : '#334155',
                      }}
                      replacementImageStyle={{
                        width: 32,
                        height: 32,
                        borderRadius: 16,
                      }}
                    />
                  </View>
                ))}
                {item.team.length > 5 && (
                  <View style={{
                    width: 32,
                    height: 32,
                    borderRadius: 16,
                    backgroundColor: theme ? '#e2e8f0' : '#334155',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <SizableText fontSize={10} color={theme ? "#64748b" : "#94a3b8"}>
                      +{item.team.length - 5}
                    </SizableText>
                  </View>
                )}
              </XStack>
            </View>
          )}
        </View>

        {/* Action Buttons */}
        <View style={{
          flexDirection: showCompactMode ? 'column' : 'row',
          padding: showCompactMode ? 12 : 16,
          gap: showCompactMode ? 8 : 12,
          borderTopWidth: 1,
          borderTopColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
        }}>
          {/* Primary Action */}
          <Button
            onPress={handleNavigateToProject}
            customStyle={{
              flex: 1,
              minHeight: 40,
              borderRadius: 8,
              backgroundColor: theme ? '#3b82f6' : '#1d4ed8',
            }}
            buttonText={t('dataStoreFindDataRecentlyAccessedDataOpenButton1') || 'Open Project'}
          />
        </View>
      </View>
    )
  }

  return (
    <View style={{ overflow: 'hidden', flex: 1 }}>
      <ProjectItem item={item} />
    </View>
  )
};

export default memo(RenderProject);